import React, { useState } from 'react';
import { X, Loader2, Database, CheckCircle, AlertTriangle, Shield, Eye, Play, Package } from 'lucide-react';
import { MaintenanceService, type MigracionResponse, type CrearLotesResponse } from '../../services/maintenance/maintenance.service';

type MaintenanceType = 'none' | 'pedidos' | 'lotes';

export const MigrarLotesModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [adminKey, setAdminKey] = useState('');
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType>('none');
  const [step, setStep] = useState<'key' | 'dryrun' | 'confirm' | 'done'>('key');
  const [loading, setLoading] = useState(false);
  const [resultMigracion, setResultMigracion] = useState<MigracionResponse | null>(null);
  const [resultLotes, setResultLotes] = useState<CrearLotesResponse | null>(null);
  const [error, setError] = useState('');

  const reset = () => {
    setAdminKey('');
    setMaintenanceType('none');
    setStep('key');
    setResultMigracion(null);
    setResultLotes(null);
    setError('');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const runDryRun = async () => {
    if (!adminKey.trim()) {
      setError('Ingresa la clave de administrador');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (maintenanceType === 'pedidos') {
        const res = await MaintenanceService.migrarLotes(adminKey.trim(), false);
        setResultMigracion(res);
        setStep('confirm');
      } else if (maintenanceType === 'lotes') {
        const res = await MaintenanceService.crearLotesStock(adminKey.trim(), false);
        setResultLotes(res);
        if (res.status === 'ERROR') {
          setStep('dryrun');
        } else {
          setStep('confirm');
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al ejecutar DRY-RUN');
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setLoading(true);
    setError('');
    try {
      if (maintenanceType === 'pedidos') {
        const res = await MaintenanceService.migrarLotes(adminKey.trim(), true);
        setResultMigracion(res);
      } else if (maintenanceType === 'lotes') {
        const res = await MaintenanceService.crearLotesStock(adminKey.trim(), true);
        setResultLotes(res);
      }
      setStep('done');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al aplicar');
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
              <Database className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Mantenimiento de lotes</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Herramientas temporales para migración de inventario
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* STEP 0: Choose maintenance type */}
          {step === 'key' && maintenanceType === 'none' && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800 dark:text-amber-300">
                    <p className="font-semibold mb-1">Acción protegida</p>
                    <p>Estas operaciones modifican datos. Asegúrate de tener un backup reciente.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Card: Migrar pedidos */}
                <button
                  onClick={() => setMaintenanceType('pedidos')}
                  className="text-left p-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-500 bg-white dark:bg-gray-800 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-3 group-hover:bg-blue-200 dark:group-hover:bg-blue-800">
                    <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">1. Migrar pedidos históricos</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Asigna lotes FIFO a pedidos antiguos que no tienen loteId. Corre esto primero si ya ejecutaste el paso 2.
                  </p>
                </button>

                {/* Card: Crear lotes */}
                <button
                  onClick={() => setMaintenanceType('lotes')}
                  className="text-left p-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-500 bg-white dark:bg-gray-800 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mb-3 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800">
                    <Package className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">2. Crear lotes de stock existente</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Para productos con stock pero sin lotes, crea lotes iniciales en la presentación principal.
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* STEP 1: Enter key */}
          {step === 'key' && maintenanceType !== 'none' && (
            <div className="space-y-4">
              <button
                onClick={() => setMaintenanceType('none')}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
              >
                ← Volver
              </button>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                  {maintenanceType === 'pedidos'
                    ? 'Vas a migrar pedidos históricos asignándoles lotes FIFO.'
                    : 'Vas a crear lotes iniciales para productos con stock pero sin lotes registrados.'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Clave de administrador (X-Admin-Key)
                </label>
                <input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Ingresa la clave secreta"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>

              <button
                onClick={runDryRun}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Ejecutar análisis (DRY-RUN)
                  </>
                )}
              </button>
            </div>
          )}

          {/* STEP 2: Results */}
          {(step === 'dryrun' || step === 'confirm') && (
            <div className="space-y-4">
              {maintenanceType === 'pedidos' && resultMigracion && (
                <MigracionResults res={resultMigracion} />
              )}
              {maintenanceType === 'lotes' && resultLotes && (
                <CrearLotesResults res={resultLotes} />
              )}

              {step === 'confirm' && resultMigracion?.status === 'OK' && (
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                  <span className="font-semibold">¿Estás seguro?</span> Se actualizarán{' '}
                  <strong>{resultMigracion.resumen.updatesPlanificados}</strong> items de pedidos históricos. Esta acción no se puede deshacer.
                </div>
              )}
              {step === 'confirm' && resultLotes?.status === 'OK' && (
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                  <span className="font-semibold">¿Estás seguro?</span> Se crearán{' '}
                  <strong>{resultLotes.resumen.lotesPlanificados}</strong> lotes iniciales. Esta acción no se puede deshacer.
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  Cerrar
                </button>

                {step === 'confirm' && (
                  <button
                    onClick={runApply}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        Aplicar cambios
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Done */}
          {step === 'done' && (
            <div className="space-y-4">
              <div className="p-6 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-center">
                <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center mb-3">
                  <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-emerald-800 dark:text-emerald-300 mb-1">
                  Operación completada
                </h3>
                {maintenanceType === 'pedidos' && resultMigracion && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    Se actualizaron <strong>{resultMigracion.resumen.actualizados}</strong> items de pedidos históricos.
                  </p>
                )}
                {maintenanceType === 'lotes' && resultLotes && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    Se crearon <strong>{resultLotes.resumen.lotesCreados}</strong> lotes de stock existente.
                  </p>
                )}
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm text-blue-800 dark:text-blue-300">
                <p className="font-semibold mb-1">Recuerda:</p>
                <p>Una vez que termines todas las migraciones, elimina el endpoint de mantenimiento del backend.</p>
              </div>

              <button
                onClick={handleClose}
                className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
              >
                Finalizar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function MigracionResults({ res }: { res: MigracionResponse }) {
  return (
    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold mb-3">
        <CheckCircle className="w-5 h-5" />
        Análisis completado
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
          <p className="text-gray-500 dark:text-gray-400">Pedidos totales</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{res.resumen.totalPedidos}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
          <p className="text-gray-500 dark:text-gray-400">Históricos</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{res.resumen.pedidosHistoricos}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
          <p className="text-gray-500 dark:text-gray-400">A asignar</p>
          <p className="text-xl font-bold text-emerald-600">{res.resumen.updatesPlanificados}</p>
        </div>
      </div>
      {res.resumen.noAsignados > 0 && (
        <div className="mt-3 text-xs text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 rounded-lg p-3 max-h-32 overflow-y-auto">
          <div className="flex items-center gap-2 font-semibold mb-1">
            <AlertTriangle className="w-4 h-4" />
            {res.resumen.noAsignados} items no asignados (stock insuficiente en lotes)
          </div>
          <p className="mb-1 text-amber-700 dark:text-amber-400">
            Estos items quedarán sin lote. Es normal si los pedidos fueron antes de la creación de lotes.
          </p>
          {res.noAsignados?.map((a, i) => (
            <p key={i} className="text-amber-700 dark:text-amber-400">• {a}</p>
          ))}
        </div>
      )}
      {res.advertencias.length > 0 && (
        <div className="mt-3 text-xs text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 rounded-lg p-3 max-h-32 overflow-y-auto">
          <p className="font-semibold mb-1">Advertencias:</p>
          {res.advertencias.map((a, i) => (
            <p key={i}>• {a}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function CrearLotesResults({ res }: { res: CrearLotesResponse }) {
  if (res.status === 'ERROR') {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-semibold mb-2">
          <AlertTriangle className="w-5 h-5" />
          Se encontraron errores
        </div>
        <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 max-h-40 overflow-y-auto">
          {res.errores.map((e, i) => (
            <li key={i}>• {e}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold mb-3">
        <CheckCircle className="w-5 h-5" />
        Análisis completado
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
          <p className="text-gray-500 dark:text-gray-400">Productos sin lotes</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{res.resumen.productosSinLotes}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
          <p className="text-gray-500 dark:text-gray-400">Lotes a crear</p>
          <p className="text-xl font-bold text-emerald-600">{res.resumen.lotesPlanificados}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
          <p className="text-gray-500 dark:text-gray-400">Errores</p>
          <p className="text-xl font-bold text-rose-600">{res.resumen.errores}</p>
        </div>
      </div>
      {res.muestra.length > 0 && (
        <div className="mt-3 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg p-3 max-h-32 overflow-y-auto">
          <p className="font-semibold mb-1">Ejemplos:</p>
          {res.muestra.map((m, i) => (
            <p key={i}>• {m.producto}: {m.cantidad} unidades (costo: {m.costoUnitario ?? 'N/A'})</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default MigrarLotesModal;
